import {Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../../web/validation";
import {Entity} from "../entity";
import {FieldMatch} from "../search/search";
import {SortFieldSpec} from "../search/sort-field-spec";
import {PageSpec} from "../search/page-spec";
import {ServiceEventType, toServiceEventType} from "./service-event-type";
import {getDatabaseCodeTransformer} from "../transfomers/transformers";
import {adjustFromTimeZoneSafely} from "../../date/date";
import {ServiceEventTargetCodec, ServiceEventTargetEntity} from "./service-event-target";
import {ServiceEventCloseoutPolicy, toServiceEventCloseoutPolicy} from "./service-event-closeout-policy";
import {ServiceEventCloseoutStatus, toServiceEventCloseoutStatus} from "./service-event-closeout-status";

export const ServiceEventCodec = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    companyId: t.string,
    regionId: t.string,
    eventType: t.string,
    startDttm: t.number,
    closeoutPolicy: t.string,
    closeoutStatus: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
  t.partial({
    name: t.string,
    severityLevel: t.number,
    source: t.string,
    externalId: t.string,
    endDttm: t.number,
    footprintGeom: t.any,
    note: t.string,
    closeoutDttm: t.number,
    createdById: t.string,
    lastModifiedById: t.string,

    companyName: t.string,
    targets: t.array(ServiceEventTargetCodec)
  }),
]);

export type ServiceEvent = t.TypeOf<typeof ServiceEventCodec>;

@Exclude()
export class ServiceEventEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  companyId!: string;

  @Expose()
  companyName?: string;

  @Expose()
  regionId!: string;

  @Transform(getDatabaseCodeTransformer(toServiceEventType))
  @Expose()
  eventType!: ServiceEventType;

  @Expose()
  name!: string;

  @Expose()
  severityLevel?: number;

  @Expose()
  source?: string;

  @Expose()
  externalId?: string;

  @Expose()
  startDttm!: number;

  @Expose()
  endDttm?: number;

  @Transform(getDatabaseCodeTransformer(toServiceEventCloseoutPolicy))
  @Expose()
  closeoutPolicy!: ServiceEventCloseoutPolicy;

  @Transform(getDatabaseCodeTransformer(toServiceEventCloseoutStatus))
  @Expose()
  closeoutStatus!: ServiceEventCloseoutStatus;

  @Expose()
  closeoutDttm?: number;

  @Expose()
  footprintGeom?: any;

  @Expose()
  note?: string;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  @Expose()
  createDttm!: number;

  @Expose()
  lastModifiedDttm!: number;

  @Expose()
  @Type(() => ServiceEventTargetEntity)
  targets?: ServiceEventTargetEntity[]

  readonly entityType = 'SVCEVENT';

  get displayName(): Readonly<string> {
    return this.name ?? this.id;
  }

  /**
   * Constructor for ServiceEventEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: ServiceEvent): ServiceEventEntity {
    return plainToInstance(ServiceEventEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<ServiceEvent> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ServiceEventCodec, object);
  }

  asTypeValue(): ServiceEvent {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(ServiceEventCodec, object);
  }

  isActive = (dttm: number = new Date().getTime()): boolean => {
    const adjustedStartDttm = adjustFromTimeZoneSafely(this.startDttm)?.getTime()
    const adjustedEndDttm = adjustFromTimeZoneSafely(this.endDttm)?.getTime()

    const meetsStartThresh = adjustedStartDttm == null || adjustedStartDttm <= dttm
    const meetsEndThresh = adjustedEndDttm == null || adjustedEndDttm > dttm

    return meetsStartThresh && meetsEndThresh
  }
}

export interface ServiceEventSearchCriteria {
  fromDttm?: number;
  toDttm?: number;
  ids?: string[]
  company?: FieldMatch;
  region?: FieldMatch;
  createdBy?: FieldMatch;
  types?: string[]
  sort?: SortFieldSpec[]
  page?: PageSpec
}

export interface ServiceEventCreateDTO {
  companyId: string;
  regionId: string;
  eventType: string;
  name: string;
  severityLevel?: number;
  source?: string;
  externalId?: string;
  startDttm: number;
  endDttm?: number;
  footprintGeom?: any;
  note?: string
  targets?: ServiceEventCreateTargetDTO[]
}

export interface ServiceEventCreateTargetDTO {
  targetMode: string;
  objectId: string;
  objectType: string;
  reason?: string;
}

export const ServiceEventBadgeSummaryCodec = t.intersection([
  t.type({
    totalEvents: t.number,
    ongoingEvents: t.number,
    completedEvents: t.number,
    recentEvents: t.array(ServiceEventCodec),
  }),
  t.partial({

  }),
]);

export type ServiceEventBadgeSummary = t.TypeOf<typeof ServiceEventBadgeSummaryCodec>;

export const ServiceEventLocationFacetHistoryCodec = t.intersection([
  t.type({
    locationStatusFacetId: t.string,
    facet: t.string,
    status: t.string,
    serviceMode: t.string,
    confidencePct: t.number,
    asOfDttm: t.number,
  }),
  t.partial({
    source: t.string,
    note: t.string,
  }),
]);

export type ServiceEventLocationFacetHistory = t.TypeOf<typeof ServiceEventLocationFacetHistoryCodec>;

export const ServiceEventLocationServiceTypeCodec = t.intersection([
  t.type({
    serviceTypeId: t.string,
    serviceTypeName: t.string,
    facetHistory: t.array(ServiceEventLocationFacetHistoryCodec),
  }),
  t.partial({}),
]);

export type ServiceEventLocationServiceType = t.TypeOf<typeof ServiceEventLocationServiceTypeCodec>;

export const ServiceEventLocationFacetCodec = t.intersection([
  t.type({
    siteId: t.string,
    siteName: t.string,
    joinedDttm: t.number,
    serviceTypes: t.array(ServiceEventLocationServiceTypeCodec),
  }),
  t.partial({
    leftDttm: t.number,
  }),
]);

export type ServiceEventLocationFacet = t.TypeOf<typeof ServiceEventLocationFacetCodec>;
